import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../../../state_manager/store.ts'
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import { CommitDealModal } from '../components/forms/CommitDealModal.tsx'
import { findCommitGaps, hasCommitGaps, isOriginationCommitTarget, type CommitGaps } from '../utils/commitGate.ts'

export interface CommitGateOptions {
  /** false = list the gaps without inline editing (the New Deal drawer). Default true. */
  inline?: boolean
  /** The New Deal drawer keeps commitments outside the form data; pass the total here. */
  commitmentAmount?: number | null
  /** The New Deal drawer only holds the company id; pass the company's industry here. */
  industry?: string | null
  /** Likewise the company's Critical Technology Area. */
  criticalTechnologyArea?: string | null
  /** The funds the drawer's un-saved commitment rows point at. Decides whether
   *  the CTA is asked for at all (SBIC funds only). */
  fundIds?: string[] | null
}

interface PendingGate {
  record: Record<string, any>
  gaps: CommitGaps
  inline: boolean
  resolve: (proceed: boolean) => void
}

/**
 * One gate for every path that moves a deal into an origination Committed
 * stage: the stage select on the drawer, full form and grid (via each field's
 * `confirmPatch`), the kanban drop, and the New Deal drawer's create. Resolves
 * true when the move may proceed — immediately for any other target stage or
 * a deal that already carries the details, otherwise after the person chose
 * "Save and commit" or "Commit anyway" in the modal; false on cancel.
 *
 * Render `holder` once in the component that owns the gate.
 */
export function useCommitDealGate() {
  const pipelineConfigs = useSelector((state: RootState) => state.globalConfigs?.pipeline_configs || [])
  const pipelineStages = useSelector((state: RootState) => state.globalConfigs?.pipeline_stages || [])
  const pipelineStatuses = useSelector((state: RootState) => state.globalConfigs?.pipeline_statuses || [])
  // is_sbic decides whether the gate asks for a Critical Technology Area.
  const funds = useSelector((state: RootState) => state.globalConfigs?.funds || [])
  const [pending, setPending] = useState<PendingGate | null>(null)

  /** True when `configId` is an origination stage whose status outcome is Won. */
  const isCommitTarget = useCallback(
    (configId: string | null | undefined): boolean =>
      isOriginationCommitTarget(configId, {
        pipeline_configs: pipelineConfigs,
        pipeline_stages: pipelineStages,
        pipeline_statuses: pipelineStatuses
      }),
    [pipelineConfigs, pipelineStages, pipelineStatuses]
  )

  const gate = useCallback(
    (
      record: Record<string, any>,
      targetConfigId: string | null | undefined,
      options?: CommitGateOptions
    ): Promise<boolean> => {
      const cfg = {
        pipeline_configs: pipelineConfigs,
        pipeline_stages: pipelineStages,
        pipeline_statuses: pipelineStatuses,
        funds
      }
      if (!isOriginationCommitTarget(targetConfigId, cfg)) return Promise.resolve(true)
      if (record?.[DealFields.pipeline_deals__stage] === targetConfigId) return Promise.resolve(true)
      const extra: {
        commitmentAmount?: number | null
        industry?: string | null
        criticalTechnologyArea?: string | null
        fundIds?: string[] | null
      } = {}
      if (options?.commitmentAmount !== undefined) extra.commitmentAmount = options.commitmentAmount
      if (options?.industry !== undefined) extra.industry = options.industry
      if (options?.criticalTechnologyArea !== undefined) {
        extra.criticalTechnologyArea = options.criticalTechnologyArea
      }
      if (options?.fundIds !== undefined) extra.fundIds = options.fundIds
      const gaps = findCommitGaps(record, Object.keys(extra).length ? extra : undefined, cfg)
      if (!hasCommitGaps(gaps)) return Promise.resolve(true)
      return new Promise<boolean>((resolve) => {
        setPending({ record, gaps, inline: options?.inline ?? true, resolve })
      })
    },
    [pipelineConfigs, pipelineStages, pipelineStatuses, funds]
  )

  const holder = pending ? (
    <CommitDealModal
      open
      record={pending.record}
      gaps={pending.gaps}
      inline={pending.inline}
      onResolve={(proceed) => {
        pending.resolve(proceed)
        setPending(null)
      }}
    />
  ) : null

  return { gate, isCommitTarget, holder }
}
